/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import { t } from '@sportninja/common/i18n'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import notificationsActions from '@sportninja/common/actions/notifications'
import { notificationTypes } from '@sportninja/common/constants/notificationsTypes'
import colors from '@sportninja/common/constants/appColors'
import { getFeedPostDate } from '@sportninja/common/utils/utils'
import req from '@sportninja/common/api/request'

import Sheet from '../Sheet'
import { font } from '../css'
import Icon from '../Icon'
import { Flex } from '../Layout'
import { Mobile } from '../Responsive'
import { Invite, InviteWrap, Refresh } from './css'
import Text from '../ts-components/Text'
import { Spin } from 'antd'

/**
 * RefreshIcon component - renders a refresh/reload SVG icon
 */
const RefreshIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='17'
    viewBox='0 0 16 17'
    fill='none'
  >
    <path
      d='M11.7633 4.73334C10.7966 3.76667 9.46992 3.16667 7.99659 3.16667C5.04992 3.16667 2.66992 5.55334 2.66992 8.5C2.66992 11.4467 5.04992 13.8333 7.99659 13.8333C10.4833 13.8333 12.5566 12.1333 13.1499 9.83334H11.7633C11.2166 11.3867 9.73659 12.5 7.99659 12.5C5.78992 12.5 3.99659 10.7067 3.99659 8.5C3.99659 6.29334 5.78992 4.50001 7.99659 4.50001C9.10326 4.50001 10.0899 4.96001 10.8099 5.68667L8.66326 7.83334H13.3299V3.16667L11.7633 4.73334Z'
      fill='#F2B400'
    />
  </svg>
)

/**
 * CheckmarkIcon component - renders a checkmark SVG icon
 */
const CheckmarkIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='17'
    viewBox='0 0 16 17'
    fill='none'
  >
    <path
      d='M11.9767 4.97L11.0367 4.03L6.81 8.25667L7.75 9.19667L11.9767 4.97ZM14.8033 4.03L7.75 11.0833L4.96333 8.30333L4.02333 9.24333L7.75 12.97L15.75 4.97L14.8033 4.03ZM0.25 9.24333L3.97667 12.97L4.91667 12.03L1.19667 8.30333L0.25 9.24333Z'
      fill='#F2B400'
    />
  </svg>
)

const notificationTypesList = Object.values(notificationTypes)

/**
 * Extracts the base URL for a given parent type and ID.
 * @param {string} parentType - The type of the parent entity (e.g., 'organization', 'team', 'schedule')
 * @param {string} parentId - The ID of the parent entity
 * @returns {string} The constructed base URL for the entity
 */
const getBaseUrl = (parentType, parentId) => {
  const urlMap = {
    organization: `/org/${parentId}`,
    team: `/team/${parentId}`,
    schedule: `/schedule/${parentId}`,
  }
  return urlMap[parentType] || ''
}

const getFeedUrl = (item = {}) => {
  try {
    if (!item?.data?.action_url) {
      return getBaseUrl(item?.data?.parent_type, item?.data?.parent_id)
    }
    const actionUrl = item?.data?.action_url || ''
    const parentType = item?.data?.parent_type || ''
    const parentId = item?.data?.parent_id || ''
    const baseUrl = getBaseUrl(parentType, parentId)
    const feedId = actionUrl.split('/').pop()
    return `${baseUrl}/feed/${feedId}`
  } catch (e) {
    console.error(e)
    return getBaseUrl(item?.data?.parent_type, item?.data?.parent_id) || ''
  }
}

const getUrl = (item = {}) => {
  const returnUrl = getFeedUrl(item)
  switch (item.type) {
    case notificationTypes.GAME_FINISHED:
    case notificationTypes.LIVE_STREAM:
    case notificationTypes.UPCOMING_GAME:
    case notificationTypes.ATTENDANCE:
    case notificationTypes.GAME_SCORED_NOTIFICATION:
    case notificationTypes.GAME_STATUS_UPDATE:
    case notificationTypes.GAME_RESCHEDULED_UPDATE:
      return `/game/${item?.data?.entity_id}`
    case notificationTypes.PLAYER_SUSPENDED:
    case notificationTypes.PLAYER_SUSPENDED_COMPLETED:
      return '/profile/suspensions'
    case notificationTypes.INVITATION:
      return `/invitations/${item?.data?.invitation_id}`
    case notificationTypes.SOCIAL_FEED:
      return returnUrl
    default:
      return ''
  }
}

const COLORS = {
  yes: colors.GREEN_GOBLIN,
  maybe: colors.AMERICAN_YELLOW,
  no: colors.FIERY_RED,
}

// TODO: Use the map that exists in common that Felipe made
const TYPE_TO_ATTENDANCE_STATUS = {
  yes: 0,
  maybe: 2,
  no: 1,
}

const AttendanceButton = ({ iconName, loading, onClick, currStatus, type }) => {
  const attendanceStatus = TYPE_TO_ATTENDANCE_STATUS[type]
  const isActiveStatus = currStatus === attendanceStatus
  const color = isActiveStatus ? COLORS[type] : colors.ATTENDANCE_GRAY

  const [clicked, setClicked] = useState(false)

  return (
    <button
      disabled={loading}
      type='button'
      onClick={async () => {
        if (!loading) {
          setClicked(true)
          await onClick(isActiveStatus ? null : attendanceStatus)
          setClicked(false)
        }
      }}
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
        min-width: 90px;
        height: 40px;
        margin-right: 8px;
        border-radius: 4px;
        border: 1px solid ${color};
        color: ${color};
        text-transform: uppercase;
        ${font.body};
        font-weight: 700;
        cursor: pointer;

        ${!isActiveStatus &&
        css`
          &:hover,
          &:hover i {
            border-color: white;
            color: white;
          }
        `}

        opacity: ${loading ? 0.5 : 1};
      `}
    >
      {clicked ? (
        <Spin size='small' />
      ) : (
        <>
          <Icon
            name={iconName}
            color={color}
            fontSize={24}
            css={css`
              margin-right: 8px;
            `}
          />
          {type}
        </>
      )}
    </button>
  )
}

const AttendanceSelector = ({
  item: { id, data } = {},
  setNotificationAsRead,
}) => {
  const { attendance_status, entity_id, game_player_roster_uid, player_uid } =
    data || {}

  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(attendance_status)
  const [error, setError] = useState(false)

  const setAttendance = async (newStatus) => {
    setLoading(true)
    try {
      await req(
        `/games/${entity_id}/rosters/${game_player_roster_uid}/players/${player_uid}/attendance`,
        {
          method: 'PUT',
          body: JSON.stringify({
            attendance_status: newStatus,
            notification_id: id,
          }),
        }
      )
      await setNotificationAsRead(id)
      setLoading(false)
      setStatus(newStatus)
    } catch (e) {
      setError(true)
      setTimeout(() => {
        setError(false)
        setLoading(false)
      }, 3000)
    }
  }

  return (
    <Flex
      css={css`
        position: relative;
        margin: 12px 0;
      `}
    >
      <AttendanceButton
        type='yes'
        currStatus={status}
        onClick={setAttendance}
        loading={loading}
        iconName='check-circle'
      />
      <AttendanceButton
        type='maybe'
        currStatus={status}
        onClick={setAttendance}
        loading={loading}
        iconName='question-circle'
      />
      <AttendanceButton
        type='no'
        currStatus={status}
        onClick={setAttendance}
        loading={loading}
        iconName='times-circle'
      />
      <Flex
        alignItems='center'
        justifyContent='center'
        className={error ? 'error' : ''}
        css={css`
          position: absolute;
          top: 0;
          bottom: 0;
          width: 100%;
          max-width: 286px;
          background-color: rgba(0, 0, 0, 0.8);
          color: ${colors.ERROR};
          ${font.body}
          font-size: 13px;
          line-height: 12px;
          text-align: center;
          opacity: 0;
          transition: opacity 0.1s ease-in;
          pointer-events: none;

          &.error {
            opacity: 1;
            pointer-events: all;
          }
        `}
      >
        <Icon
          name='exclamation-triangle'
          color={colors.ERROR}
          fontSize={16}
          css={css`
            transform: translateX(18px);
          `}
        />
        <p>Oops - we had trouble setting your attendance.</p>
      </Flex>
    </Flex>
  )
}

const NotificationItem = ({
  item = {},
  onClick,
  setNotificationAsRead,
  onPressDelete,
  setNotificationOpenForDelete = () => {},
  notificationOpenForDelete = null,
}) => {
  const history = useHistory()
  const isRecognizedType = notificationTypesList.includes(item?.type)
  const isAttendance = item?.type === notificationTypes.ATTENDANCE
  const isSocialFeed = item?.type === notificationTypes.SOCIAL_FEED
  const [optionsDivVisible, setOptionsDivVisible] = useState(false)

  useEffect(() => {
    if (notificationOpenForDelete) {
      if (notificationOpenForDelete !== item?.id) {
        setOptionsDivVisible(false)
      }
    }
  }, [notificationOpenForDelete])

  const handleNotificationClick = async () => {
    if (isRecognizedType) {
      await setNotificationAsRead(item.id)
      const url = getUrl(item)
      if (url) {
        history.push(url)
      }
      onClick()
    }
  }

  return (
    <Invite
      css={css`
        gap: 8px;
        margin-bottom: 0;
        cursor: pointer;
        ${isSocialFeed &&
        css`
          border-radius: 8px;
          background: ${colors.SECONDARY_200};
          padding: 8px;
        `}
      `}
      onClick={handleNotificationClick}
    >
      <div className='nav-link'>
        <div className='title'>
          <Text variant='paragraphSmall' weight='bold' color={colors.NEUTRAL_0}>
            {item?.data?.title}
          </Text>
          <div className='pip-container'>
            <div className={!item?.read_at ? 'pip' : ''} />
          </div>
        </div>
        <div
          className='flex type'
          style={{
            alignItems: 'center',
            color: colors.NEUTRAL_0,
            opacity: 1,
          }}
        >
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_0}
          >
            {isSocialFeed && item?.data.body?.length > 200
              ? `${item?.data.body.substring(0, 200)}...`
              : item?.data.body}
          </Text>
        </div>

        {/* {isAttendance && (
        <AttendanceSelector
          item={item}
          setNotificationAsRead={setNotificationAsRead}
        />
      )} */}
        <Flex>
          <div
            className='date'
            title={dayjs(item.created_at).format('h:mm a - MMM D, YYYY')}
          >
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {getFeedPostDate(item?.created_at)}
            </Text>
          </div>
          {/* This needs a little more work. */}
          {/* {!item?.read_at && (
          <>
            {' - '}
            <button
              css={css`
                margin-left: 6px;
                font-size: 12px;
                opacity: 0.4;
                color: white;
                text-decoration: underline;
              `}
              type='button'
              onClick={async () => {
                await setNotificationAsRead(item.id)
              }}
            >
              Mark as read
            </button>
          </>
        )} */}
        </Flex>
      </div>
      <div style={{ position: 'relative', marginLeft: 'auto' }}>
        <button
          id='notification-options'
          css={css`
            margin-left: auto;
          `}
          type='button'
          onClick={(e) => {
            e.stopPropagation()
            setOptionsDivVisible(!optionsDivVisible)
            setNotificationOpenForDelete(item.id)
          }}
        >
          <Icon name='ellipsis-v' fontSize={16} />
        </button>
        {optionsDivVisible && (
          <div
            css={css`
              position: absolute;
              right: 100%; // position to the left of the button
              background-color: ${colors.HEADER};
              display: flex;
              flex-direction: row;
              gap: 8px;
              padding: 12px;
              border-radius: 8px;
              box-shadow: 0px 0px 10px 1px rgba(0, 0, 0, 0.15);
              cursor: pointer;
              justify-content: center;
              align-items: center;
              z-index: 10000;
            `}
            onClick={async (e) => {
              e.stopPropagation()
              setOptionsDivVisible(false)
              await onPressDelete()
            }}
          >
            <Icon name='trash' fontSize={16} />
            <span
              css={css`
                font-size: 14px;
                color: white;
              `}
            >
              Delete
            </span>
          </div>
        )}
      </div>
    </Invite>
  )
}

const sortByDate = (a, b) => {
  const aDate = dayjs(a.created_at)
  const bDate = dayjs(b.created_at)

  if (aDate.isAfter(bDate)) {
    return -1
  }
  if (aDate.isBefore(bDate)) {
    return 1
  }
  return 0
}

// A separate component, so that we can properly manage lifecycle events for
// requesting invitations (i.e. on mount, and when isOpen === true)
let Notifications = ({
  toggle,
  setNotificationAsRead,
  setAllAsRead,
  refreshNotificationCount,
  isOpen,
}) => {
  const [list, setList] = useState([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [notificationLoading, setNotificationLoading] = useState(false)
  const [notificationError, setNotificationsError] = useState(null)
  const [notificationOpenForDelete, setNotificationOpenForDelete] =
    useState(null)

  useEffect(() => {
    if (isOpen === false) {
      refreshNotificationCount()
    }
  }, [isOpen])

  const readNotifications = useCallback(
    async (nextPage) => {
      try {
        setNotificationLoading(true)
        setNotificationsError(null)
        const { data, meta } = await req(
          `/notifications?page=${nextPage || page}`
        )
        if (data && data.length > 0) {
          setNotifications(data)
          setPagination(meta?.pagination)
        }
      } catch (e) {
        console.error(e)
        setNotificationsError(e?.message || 'Error loading notifications')
      } finally {
        setNotificationLoading(false)
      }
    },
    [page]
  )

  const readMoreNotifications = useCallback(
    async (nextPage, refresh = false) => {
      try {
        setNotificationsError(null)
        const { data, meta } = await req(
          `/notifications?page=${nextPage || page}`
        )
        if (data && data.length > 0) {
          if (refresh) {
            setNotifications(data)
          } else {
            setNotifications((prev) => [...prev, ...data])
          }
          setPagination(meta?.pagination)
        }
      } catch (e) {
        console.error(e)
        setNotificationsError(e?.message || 'Error loading notifications')
      }
    },
    [page]
  )

  useEffect(() => {
    readNotifications(1)
  }, [])

  useEffect(() => {
    if (notifications && notifications.length > 0) {
      setList(notifications.sort(sortByDate))
    }
  }, [notifications])

  return (
    <InviteWrap>
      <Flex
        row
        alignItems='center'
        css={css`
          margin-bottom: 16px;
          border-bottom: 1px solid ${colors.SECONDARY_200};
          padding-bottom: 8px;
        `}
      >
        <Flex row alignItems='center'>
          <Text
            variant='heading5'
            weight='semiBold'
            color={colors.NEUTRAL_0}
            style={`
                margin-right: 10px;
                text-transform: uppercase;
              `}
          >
            Notifications
          </Text>
          <button
            onClick={async () => await readNotifications(1)}
            title='Refresh'
            css={css`
              cursor: pointer;
              justify-content: center;
              align-items: center;
              margin-top: 2px;
            `}
          >
            <RefreshIcon />
          </button>
        </Flex>
        {list && list?.length > 0 ? (
          <button
            onClick={async () => {
              setNotificationLoading(true)
              await setAllAsRead()
              await readNotifications(1)
            }}
            css={css`
              margin-right: 12px;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <Text
                variant='paragraphXSmall'
                weight='medium'
                color={colors.PRIMARY}
              >
                Mark all as read
              </Text>
              <CheckmarkIcon />
            </div>
          </button>
        ) : null}
      </Flex>
      {notificationLoading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 36px;
          `}
        >
          <Spin size='small' />
        </div>
      ) : (
        <ul className='notification-list'>
          {list.length > 0 ? (
            <div>
              {list.sort(sortByDate).map((item) => {
                return (
                  <>
                    <NotificationItem
                      key={item.id}
                      item={item}
                      setNotificationAsRead={setNotificationAsRead}
                      onClick={async () => {
                        await setNotificationAsRead(item.id)
                        toggle()
                      }}
                      onPressDelete={async () => {
                        try {
                          await req(`/notifications/${item.id}`, {
                            method: 'DELETE',
                          })
                          await readNotifications(1)
                        } catch (e) {
                          console.error(e)
                        }
                      }}
                      setNotificationOpenForDelete={
                        setNotificationOpenForDelete
                      }
                      notificationOpenForDelete={notificationOpenForDelete}
                    />
                    <div
                      css={css`
                        height: 1px;
                        background-color: ${colors.SECONDARY_200};
                        margin: 8px 0;
                      `}
                    />
                  </>
                )
              })}
              {pagination?.total_pages > page ? (
                <div>
                  <button
                    css={css`
                      display: flex;
                      align-self: center;
                      align-items: center;
                      justify-content: center;
                      width: 30%;
                      height: 36px;
                      margin-top: 24px;
                      border: 2px solid ${colors.DEFAULT_FLAIR};
                      border-radius: 8px;
                      color: ${colors.WHITE};
                      ${font.title}
                      font-weight: 700;
                      font-size: 16px;
                      ${notificationLoading &&
                      css`
                        cursor: wait;
                      `}
                    `}
                    onClick={async () => {
                      await readMoreNotifications(page + 1)
                      setPage(page + 1)
                    }}
                  >
                    Load more
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <li>
              <div style={{ textAlign: 'center', marginTop: 56 }}>
                {notificationError
                  ? t('errors:anErrorOccurred')
                  : t('Web:noNotifications')}
              </div>
              <Refresh onClick={async () => await readNotifications(1, true)}>
                {t('common:refresh')}?
              </Refresh>
            </li>
          )}
        </ul>
      )}
    </InviteWrap>
  )
}

Notifications.propTypes = {
  notifications: PropTypes.array.isRequired,
  setNotificationAsRead: PropTypes.func.isRequired,
  setAllAsRead: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
}

const mapStateToProps = (_) => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {
    setNotificationAsRead: bindActionToPromise(
      dispatch,
      notificationsActions.readNotification.request
    ),
    setAllAsRead: bindActionToPromise(
      dispatch,
      notificationsActions.readAll.request
    ),
  }
}

Notifications = connect(mapStateToProps, mapDispatchToProps)(Notifications)

const NotificationsWrapper = ({
  Button,
  refreshNotificationCount = () => {},
}) => {
  return (
    <Mobile>
      {(isMobile) => {
        return (
          <Sheet
            button={Button}
            position='right'
            width={isMobile ? '320px' : '500px'}
            css={css`
              .scrolling-container {
                padding: 50px 20px 48px !important;
              }
              background: ${colors.SECONDARY_300};
            `}
          >
            {(toggle, _, isOpen) => (
              <Notifications
                toggle={toggle}
                isOpen={isOpen}
                refreshNotificationCount={refreshNotificationCount}
              />
            )}
          </Sheet>
        )
      }}
    </Mobile>
  )
}

NotificationsWrapper.propTypes = {
  Button: PropTypes.func.isRequired,
}

export default NotificationsWrapper
